import { useEffect, useState } from 'react'
import type { ChangeEvent, ClipboardEvent, KeyboardEvent } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useUpdateUsuario, useUpdatePassword } from '@/hooks/useAuth'
import { UpdateUsuarioRequest, UpdatePasswordRequest } from '@/types'
import { useForm } from 'react-hook-form'
import UploadFotoPerfil from '@/components/UploadFotoPerfil'
import { 
  User, 
  Mail, 
  Phone, 
  CreditCard, 
  MapPin, 
  Calendar, 
  Shield, 
  Edit, 
  Eye, 
  EyeOff,
  Lock
} from 'lucide-react'

const formatCpf = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 11)

  const part1 = digits.slice(0, 3)
  const part2 = digits.slice(3, 6)
  const part3 = digits.slice(6, 9)
  const part4 = digits.slice(9, 11)

  let formatted = part1
  if (part2) formatted = `${formatted}.${part2}`
  if (part3) formatted = `${formatted}.${part3}`
  if (part4) formatted = `${formatted}-${part4}`

  return formatted
}

const Profile = () => {
  const { user, updateUser } = useAuth()
  const [isEditing, setIsEditing] = useState(false)
  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  
  const updateMutation = useUpdateUsuario()
  const updatePasswordMutation = useUpdatePassword()

  const { 
    register: registerProfile, 
    handleSubmit: handleSubmitProfile, 
    formState: { errors: errorsProfile },
    setValue: setProfileValue
  } = useForm<UpdateUsuarioRequest>({
    defaultValues: {
      nome: user?.nome,
      email: user?.email,
      cpf: formatCpf(user?.cpf || ''),
      telefone: user?.telefone,
      dataNascimento: user?.dataNascimento,
      endereco: user?.endereco
    }
  })

  const { register: registerPassword, handleSubmit: handleSubmitPassword, formState: { errors: errorsPassword }, reset: resetPassword } = useForm<UpdatePasswordRequest>()

  const handleUpdateProfile = async (data: UpdateUsuarioRequest) => {
    if (!user) return
    
    try {
      const updatedUser = await updateMutation.mutateAsync({ id: user.id, data })
      updateUser(updatedUser)
      setIsEditing(false)
    } catch (error) {
      // Erro já tratado no hook
    }
  }

  const handleUpdatePassword = async (data: UpdatePasswordRequest) => {
    if (!user) return
    
    try {
      await updatePasswordMutation.mutateAsync({ id: user.id, data })
      setShowPasswordForm(false)
      resetPassword()
    } catch (error) {
      // Erro já tratado no hook
    }
  }

  useEffect(() => {
    if (user?.cpf) {
      setProfileValue('cpf', formatCpf(user.cpf))
    }
  }, [user?.cpf, setProfileValue])

  const cpfRegister = registerProfile('cpf', {
    pattern: {
      value: /^\d{3}\.\d{3}\.\d{3}-\d{2}$/,
      message: 'CPF deve estar no formato 000.000.000-00'
    }
  })

  const handleCpfChange = (event: ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCpf(event.target.value)
    event.target.value = formatted
    setProfileValue('cpf', formatted, { shouldValidate: true, shouldDirty: true })
    cpfRegister.onChange(event)
  }

  const handleCpfKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.ctrlKey || event.metaKey) return

    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End',
      'Enter'
    ]

    if (allowedKeys.includes(event.key)) return
    if (/^\d$/.test(event.key)) return

    event.preventDefault()
  }

  const handleCpfPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault()
    const pasted = event.clipboardData.getData('Text')
    const formatted = formatCpf(pasted)

    const target = event.target as HTMLInputElement
    target.value = formatted
    setProfileValue('cpf', formatted, { shouldValidate: true, shouldDirty: true })
    cpfRegister.onChange({
      target,
      currentTarget: target
    } as unknown as ChangeEvent<HTMLInputElement>)
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Meu Perfil</h1>
        <p className="text-gray-600 mt-2">
          Gerencie suas informações pessoais
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="card">
            <div className="text-center">
              <UploadFotoPerfil
                userId={user.id}
                fotoPerfilAtual={user.fotoPerfil}
                onUploadSuccess={(fotoUrl) => {
                  // Atualizar o usuário no contexto
                  updateUser({ ...user, fotoPerfil: fotoUrl || undefined })
                }}
              />
              <h3 className="text-xl font-semibold text-gray-900 mt-4">{user.nome}</h3>
              <p className="text-gray-600">{user.email}</p>
              <div className="mt-4">
                <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                  user.role === 'ADMIN' 
                    ? 'bg-purple-100 text-purple-800' 
                    : 'bg-blue-100 text-blue-800'
                }`}>
                  {user.role === 'ADMIN' ? 'Administrador' : 'Usuário'}
                </span>
              </div>
              <div className="mt-4">
                <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                  user.ativo 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {user.ativo ? 'Ativo' : 'Inativo'}
                </span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card mt-6">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Ações Rápidas</h4>
            <div className="space-y-3">
              <button
                onClick={() => setIsEditing(!isEditing)}
                className="w-full btn btn-secondary flex items-center justify-center gap-2"
              >
                <Edit className="h-4 w-4" />
                {isEditing ? 'Cancelar Edição' : 'Editar Perfil'}
              </button>
              
              <button
                onClick={() => setShowPasswordForm(!showPasswordForm)}
                className="w-full btn btn-secondary flex items-center justify-center gap-2"
              >
                <Lock className="h-4 w-4" />
                {showPasswordForm ? 'Cancelar' : 'Alterar Senha'}
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-2">
          {/* Informações Pessoais */}
          <div className="card mb-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Informações Pessoais</h2>
              {!isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="btn btn-primary flex items-center gap-2"
                >
                  <Edit className="h-4 w-4" />
                  Editar
                </button>
              )}
            </div>

            {isEditing ? (
              <form onSubmit={handleSubmitProfile(handleUpdateProfile)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome Completo *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        {...registerProfile('nome', { required: 'Nome é obrigatório' })}
                        type="text"
                        className="input pl-10"
                        placeholder="Digite seu nome completo"
                      />
                    </div>
                    {errorsProfile.nome && (
                      <p className="mt-1 text-sm text-red-600">{errorsProfile.nome.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      E-mail *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        {...registerProfile('email', {
                          required: 'E-mail é obrigatório',
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'E-mail inválido'
                          }
                        })}
                        type="email"
                        className="input pl-10"
                        placeholder="Digite seu e-mail"
                      />
                    </div>
                    {errorsProfile.email && (
                      <p className="mt-1 text-sm text-red-600">{errorsProfile.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CPF
                    </label>
                    <div className="relative">
                      <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                      {...cpfRegister}
                      onChange={handleCpfChange}
                      onKeyDown={handleCpfKeyDown}
                      onPaste={handleCpfPaste}
                        type="text"
                        className="input pl-10"
                        placeholder="000.000.000-00"
                      inputMode="numeric"
                      maxLength={14}
                      />
                    </div>
                    {errorsProfile.cpf && (
                      <p className="mt-1 text-sm text-red-600">{errorsProfile.cpf.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Telefone
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        {...registerProfile('telefone')}
                        type="tel"
                        className="input pl-10"
                        placeholder="(00) 00000-0000"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Data de Nascimento
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        {...registerProfile('dataNascimento')}
                        type="date"
                        className="input pl-10"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-4">
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="btn btn-secondary"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={updateMutation.isLoading}
                    className="btn btn-primary disabled:opacity-50"
                  >
                    {updateMutation.isLoading ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-center space-x-3">
                  <User className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Nome</p>
                    <p className="text-sm text-gray-900">{user.nome}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Mail className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">E-mail</p>
                    <p className="text-sm text-gray-900">{user.email}</p>
                  </div>
                </div>

                {user.telefone && (
                  <div className="flex items-center space-x-3">
                    <Phone className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">Telefone</p>
                      <p className="text-sm text-gray-900">{user.telefone}</p>
                    </div>
                  </div>
                )}

                {user.cpf && (
                  <div className="flex items-center space-x-3">
                    <CreditCard className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">CPF</p>
                      <p className="text-sm text-gray-900">{user.cpf}</p>
                    </div>
                  </div>
                )}

                {user.dataNascimento && (
                  <div className="flex items-center space-x-3">
                    <Calendar className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">Data de Nascimento</p>
                      <p className="text-sm text-gray-900">
                        {new Date(user.dataNascimento).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Endereço */}
          {user.endereco && (
            <div className="card mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Endereço</h2>
              <div className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 text-gray-400 mt-1" />
                <div className="text-sm text-gray-900">
                  <p className="font-medium">
                    {user.endereco.logradouro}, {user.endereco.numero}
                    {user.endereco.complemento && `, ${user.endereco.complemento}`}
                  </p>
                  <p className="text-gray-600">
                    {user.endereco.bairro} - {user.endereco.cidade}/{user.endereco.estado}
                  </p>
                  <p className="text-gray-600">CEP: {user.endereco.cep}</p>
                </div>
              </div>
            </div>
          )}

          {/* Alterar Senha */}
          {showPasswordForm && (
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Alterar Senha</h2>
              <form onSubmit={handleSubmitPassword(handleUpdatePassword)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nova Senha *
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      {...registerPassword('novaSenha', {
                        required: 'Nova senha é obrigatória',
                        minLength: {
                          value: 6,
                          message: 'Senha deve ter pelo menos 6 caracteres'
                        }
                      })}
                      type={showPassword ? 'text' : 'password'}
                      className="input pl-10 pr-10"
                      placeholder="Digite a nova senha"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 transform -translate-y-1/2"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {errorsPassword.novaSenha && (
                    <p className="mt-1 text-sm text-red-600">{errorsPassword.novaSenha.message}</p>
                  )}
                </div>

                <div className="flex justify-end gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordForm(false)
                      resetPassword()
                    }}
                    className="btn btn-secondary"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={updatePasswordMutation.isLoading}
                    className="btn btn-primary disabled:opacity-50"
                  >
                    {updatePasswordMutation.isLoading ? 'Alterando...' : 'Alterar Senha'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Informações do Sistema */}
          <div className="card">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Informações do Sistema</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">ID do Usuário</p>
                <p className="text-sm text-gray-900 font-mono">{user.id}</p>
              </div>
              
              <div>
                <p className="text-sm font-medium text-gray-500">Função</p>
                <div className="flex items-center space-x-2">
                  <Shield className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-900">
                    {user.role === 'ADMIN' ? 'Administrador' : 'Usuário'}
                  </span>
                </div>
              </div>
              
              <div>
                <p className="text-sm font-medium text-gray-500">Data de Criação</p>
                <p className="text-sm text-gray-900">
                  {new Date(user.dataCriacao).toLocaleString('pt-BR')}
                </p>
              </div>
              
              <div>
                <p className="text-sm font-medium text-gray-500">Última Atualização</p>
                <p className="text-sm text-gray-900">
                  {new Date(user.dataAtualizacao).toLocaleString('pt-BR')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Profile
